import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";
import {fetchOdPLegacy} from "../produzione/odpLegacySlice";
import {
    FiltriOdPLegacy,
    setFiltriOdPSpallaSchedulazione,
} from "components/userPreference/userPreferenceSlice";
import {ClienteLight} from "../../anagrafiche/clienti/clientiSlice";
import {Operatore} from "../personale/operatoriSlice";
import {SENZA_OPERATORE_ID} from "./gantt/GanttHeader";

export const useFiltriOdPLegacy = (operatori: Operatore[]) => {
    const dispatch = useAppDispatch();

    const filtriOdP = useSelector<RootState, FiltriOdPLegacy>(
        (state) => state.userPreferenceState.filtriOdPSpallaSchedulazione || {}
    );

    const [searchValue, setSearchValue] = useState<string>(
        filtriOdP?.search || ""
    );
    const [statiSelezionati, setStatiSelezionati] = useState<number[]>(
        filtriOdP?.stati || []
    );
    const [clienteSelezionato, setClienteSelezionato] = useState<ClienteLight | null>(
        filtriOdP?.cliente || null
    );
    const [repartiSelezionati, setRepartiSelezionati] = useState<number[]>(
        filtriOdP?.reparto || []
    );
    const [operatoriSelezionati, setOperatoriSelezionati] = useState<number[]>(
        (filtriOdP?.operatori || [])
            .map((codice) => operatori.find((op) => op.codice === codice)?.id)
            .filter((id): id is number => id !== undefined)
    );
    const [dataDa, setDataDa] = useState<string>(filtriOdP?.data_da || "");
    const [dataA, setDataA] = useState<string>(filtriOdP?.data_a || "");
    const [nonPerfettamenteSchedulati, setNonPerfettamenteSchedulati] = useState<boolean>(
        filtriOdP?.non_perfettamente_schedulati || false
    );

    const buildFiltri = (search: string): FiltriOdPLegacy => ({
        ...filtriOdP,
        search: search || undefined,
        stati: statiSelezionati.length > 0 ? statiSelezionati : undefined,
        cliente: clienteSelezionato,
        reparto: repartiSelezionati.length > 0 ? repartiSelezionati : undefined,
        operatori: (() => {
            // SENZA_OPERATORE_ID è un sentinel solo-Gantt: non lo passiamo al backend.
            const idsReali = operatoriSelezionati.filter((id) => id !== SENZA_OPERATORE_ID);
            if (idsReali.length === 0) return undefined;
            return idsReali
                .map((id) => operatori.find((op) => op.id === id)?.codice)
                .filter((c): c is string => c !== undefined);
        })(),
        data_da: dataDa || undefined,
        data_a: dataA || undefined,
        non_perfettamente_schedulati: nonPerfettamenteSchedulati || undefined,
        page: 1,
    });

    // Skip al primo render: l'init è già coperto dal useEffect [refreshOdp] in SchedulazionePage
    const isFirstRender = useRef(true);
    // Skip dell'effetto successivo dopo un reset (evita doppio fetch)
    const skipNextEffect = useRef(false);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (skipNextEffect.current) {
            skipNextEffect.current = false;
            return;
        }
        const nuoviFiltri = buildFiltri(searchValue);
        dispatch(setFiltriOdPSpallaSchedulazione(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
        // searchValue intenzionalmente NON nelle deps: trigger su Enter/Blur via applicaConSearch
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        statiSelezionati,
        clienteSelezionato,
        repartiSelezionati,
        operatoriSelezionati,
        dataDa,
        dataA,
        nonPerfettamenteSchedulati,
    ]);

    const applicaConSearch = () => {
        const nuoviFiltri = buildFiltri(searchValue);
        dispatch(setFiltriOdPSpallaSchedulazione(nuoviFiltri));
        dispatch(fetchOdPLegacy(nuoviFiltri));
    };

    const resetFiltri = () => {
        skipNextEffect.current = true;
        setSearchValue("");
        setStatiSelezionati([]);
        setClienteSelezionato(null);
        setRepartiSelezionati([]);
        setOperatoriSelezionati([]);
        setDataDa("");
        setDataA("");
        setNonPerfettamenteSchedulati(false);

        const filtriDefault: FiltriOdPLegacy = {
            numero_record: 20,
            page: 1,
        };
        dispatch(setFiltriOdPSpallaSchedulazione(filtriDefault));
        dispatch(fetchOdPLegacy(filtriDefault));
    };

    const toggleStato = (stato: number) => {
        setStatiSelezionati((prev) =>
            prev.includes(stato)
                ? prev.filter((s) => s !== stato)
                : [...prev, stato]
        );
    };

    return {
        searchValue,
        setSearchValue,
        statiSelezionati,
        setStatiSelezionati,
        clienteSelezionato,
        setClienteSelezionato,
        repartiSelezionati,
        setRepartiSelezionati,
        operatoriSelezionati,
        setOperatoriSelezionati,
        dataDa,
        setDataDa,
        dataA,
        setDataA,
        nonPerfettamenteSchedulati,
        setNonPerfettamenteSchedulati,
        toggleStato,
        applicaConSearch,
        resetFiltri,
    };
};
